import { PropsWithChildren, useEffect } from "react";
import DateSelect from "../common/DateSelect";
import {
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Box,
} from "@mui/material";
import useFetchDashboardValues from "./hooks/useFetchMetric";
import { useFormikContext } from "formik";
import { ClientInvestment, RecordWithEnumeratedIndex } from "../client/types";
import { Dates } from "../common/types";

import {
  CardHeading,
  CardWrapper,
  ElementWrapper,
  Error,
} from "./common-components";
import routes from "../../routes";

const InvestedClientsCardContent = () => {
  const id = "clientsWithInvestments";
  const { values, isValid } =
    useFormikContext<RecordWithEnumeratedIndex<Dates<typeof id>, number>>();

  const { loading, metric, fetchMetric } = useFetchDashboardValues<{
    clientsWithInvestments: ClientInvestment[];
  }>("invested clients");

  useEffect(() => {
    if (isValid) {
      fetchMetric({
        fromDate: values[`${id}-start-date`],
        toDate: values[`${id}-end-date`],
      });
    }
  }, [values, fetchMetric, id, isValid]);

  const element = () => {
    if (loading) {
      return <CircularProgress />;
    }

    if (typeof metric?.clientsWithInvestments.length === "number") {
      const numberOfClients = metric.clientsWithInvestments.length;
      if (numberOfClients === 0) {
        return (
          <Typography
            sx={{
              fontSize: 24,
              marginTop: 6,
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            No clients found
          </Typography>
        );
      }
      return (
        <Box>
          <Typography
            sx={{
              fontSize: 36,
              marginTop: 2,
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {numberOfClients}
          </Typography>
          <List dense={true}>
            {metric.clientsWithInvestments.map((client, index) => (
              <ListItemButton
                component="a"
                href={routes.clients.view({ id: client.id })}
                key={client.id + index}
              >
                <ListItemText primary={client.name} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      );
    }

    return <Error />;
  };

  return (
    <>
      <CardHeading heading="Clients with investments" />
      <DateSelect dateName={id} minDate={new Date("2020-01-01")} />
      <ElementWrapper>{element()}</ElementWrapper>
    </>
  );
};

const InvestedClientsCardWrapper = ({ children }: PropsWithChildren) => (
  <CardWrapper id="clientsWithInvestments">{children}</CardWrapper>
);

export const InvestedClientsCard = () => (
  <InvestedClientsCardWrapper>
    <InvestedClientsCardContent />
  </InvestedClientsCardWrapper>
);
